import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getChangeLogEntry,
  listChangeLog,
  undoChangeLog,
  type ChangeLogEntryDetail,
  type ChangeLogList,
  type ChangeLogListParams,
} from "../api/changeLog";

const KEY = "change-log";

/** Read-only admin audit feed (§L — HTTP via TanStack Query). Paged: `keepPreviousData` holds the table while a new page loads. */
export function useChangeLog(params: ChangeLogListParams = {}) {
  return useQuery<ChangeLogList>({
    queryKey: [KEY, "list", params],
    queryFn: () => listChangeLog(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/** Fetch one entry's before/after snapshots for the undo-impact diff. Enabled only when an id is set (i.e. the undo dialog is open). */
export function useChangeLogEntry(id: string | null) {
  return useQuery<ChangeLogEntryDetail>({
    queryKey: [KEY, "detail", id],
    queryFn: () => getChangeLogEntry(id as string),
    enabled: id != null,
    staleTime: 30_000,
  });
}

/** Variables for an undo: the entry id and whether to skip the conflict check. */
export interface UndoChangeLogVars {
  id: string;
  /** Skip the backend state-conflict check (the 409 guard). */
  force?: boolean;
}

/** Flip a row's status to 'undone'; refreshes the list on success. */
export function useUndoChangeLog() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, UndoChangeLogVars>({
    mutationFn: ({ id, force }) => undoChangeLog(id, force),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] });
    },
  });
}
